import { track } from "@vercel/analytics"

import type { AppBillingLifecycleState } from "@/features/billing/app-billing-lifecycle"
import {
  ENTITLEMENT_BLOCKER,
  type EntitlementBlockerCode,
} from "@/features/billing/entitlement-reason-codes"
import type { PlanId } from "@/features/billing/plans"

type BillingProps = Record<string, string | number | boolean | null | undefined>

function emit(name: string, props?: BillingProps): void {
  if (typeof window === "undefined") return
  try {
    if (!props) {
      track(name)
      return
    }
    const flat: Record<string, string | number | boolean | null | undefined> = {}
    for (const [k, v] of Object.entries(props)) {
      if (v === undefined || v === "") continue
      flat[k] = typeof v === "string" || typeof v === "number" || typeof v === "boolean" ? v : String(v)
    }
    track(name, flat as Record<string, string | number | boolean | null | undefined>)
  } catch {
    // ignore
  }
}

export function trackBillingPricingViewed(): void {
  emit("billing_pricing_viewed")
}

export function trackBillingLimitWarningShown(input: {
  meterKey: string
  heat: string
  planId: PlanId
  lifecycle: AppBillingLifecycleState
}): void {
  emit("billing_limit_warning_shown", {
    meter_key: input.meterKey,
    heat: input.heat,
    plan_id: input.planId,
    lifecycle: input.lifecycle,
  })
}

export function trackBillingLimitHit(input: {
  blockerCode: EntitlementBlockerCode
  surface: string
  planId?: PlanId
  lifecycle?: AppBillingLifecycleState | null
}): void {
  const base = {
    blocker_code: input.blockerCode,
    surface: input.surface,
    plan_id: input.planId ?? "",
    lifecycle: input.lifecycle ?? "",
  }
  switch (input.blockerCode) {
    case ENTITLEMENT_BLOCKER.EVENT_LIMIT_REACHED:
      emit("billing_event_limit_hit", base)
      break
    case ENTITLEMENT_BLOCKER.MEDIA_ITEM_LIMIT_REACHED:
      emit("billing_media_limit_hit", base)
      break
    case ENTITLEMENT_BLOCKER.STORAGE_LIMIT_REACHED:
      emit("billing_storage_limit_hit", base)
      break
    default:
      break
  }
}

export function trackBillingUpgradeModalOpened(surface: string): void {
  emit("billing_upgrade_modal_opened", { surface })
}

export function trackBillingCheckoutStarted(input: {
  kind: "new_subscription" | "plan_change" | "storage_addon"
  plan: string
  interval?: string
}): void {
  emit("billing_checkout_started", {
    kind: input.kind,
    plan: input.plan,
    interval: input.interval ?? "",
  })
}

export function trackBillingCheckoutCompleted(input: {
  kind: "plan_change_in_app"
  plan: string
}): void {
  emit("billing_checkout_completed", {
    kind: input.kind,
    plan: input.plan,
  })
}

export function trackBillingCheckoutReturn(outcome: "success" | "cancel"): void {
  emit("billing_checkout_return", { outcome })
}

export function trackBillingPortalOpened(surface: string): void {
  emit("billing_portal_opened", { surface })
}

export function trackBillingLifecycleBannerViewed(input: {
  banner: "cancel_scheduled" | "past_due" | "access_ended"
  planId: PlanId
  lifecycle: AppBillingLifecycleState
}): void {
  emit("billing_lifecycle_banner_viewed", {
    banner: input.banner,
    plan_id: input.planId,
    lifecycle: input.lifecycle,
  })
}
